import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import MUIDataTable from "mui-datatables";
import axios from "axios";
import { toast } from "react-toastify";
import { Button, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import moment from "moment";

import { fetchDrsEbtLogs } from "../../actions/ebt_logs.js";
import getQueryString from "./utils.js";

const parseXmlContent = (xmlString) => {
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(xmlString, "application/xml");
    const getText = (tag) => {
      const el = doc.getElementsByTagName(tag)[0];
      return el ? el.textContent : "";
    };
    let elements = ["Document: " + doc.documentElement.nodeName];
    // Get all direct child elements of the root
    const childElements = Array.from(doc.documentElement.childNodes).filter(node => node.nodeType === Node.ELEMENT_NODE);
    for (let i = 0; i < childElements.length; i++) {
      const childElement = childElements[i];
      const tagName = childElement.nodeName;
      const text = getText(tagName);
      elements.push(`${tagName}: ${text}`);
    }
    return elements
  } catch (e) {
    return { raw: xmlString };
  }
}
const downloadDecodedFile = (base64) => {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  const blob = new Blob([bytes], { type: "application/zip" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "file.zip";
  a.click();
  URL.revokeObjectURL(url);
};

const handleDownload = (item) => {
  const fileStr = item.content.filter(line => line.startsWith("Data: "))[0];
  if (fileStr) {
    downloadDecodedFile(fileStr.replace("Data: ", ""));
  } else {
    toast.error('Data field not found');
  }
};

const EBTDrsInbox = (props) => {
  const dispatch = useDispatch();
  const store = useSelector((state) => state.ebtLogs);

  const [dataOptions, setDataOptions] = useState("");
  const [sortColumns, setSortColumns] = useState([]);
  const [currentRowId, setCurrentRowId] = useState(null);
  const [loadingIds, setLoadingIds] = useState(new Set());

  // Load data when component mounts
  useEffect(() => {
    dispatch(fetchDrsEbtLogs(""));
  }, [dispatch]);

  // Refetch data when dataOptions changes (which happens on sort, page change, etc.)
  useEffect(() => {
    if (dataOptions !== "") {
      dispatch(fetchDrsEbtLogs(dataOptions));
    }
  }, [dataOptions, dispatch]);

  const [contentDialogOpen, setContentDialogOpen] = useState(false);
  const [contentData, setContentData] = useState([]);
  const [transactionIdForObjection, setTransactionIdForObjection] = useState(null);
  const [terminationModalOpen, setTerminationModalOpen] = useState(false);
  const [transactionIdForTermination, setTransactionIdForTermination] = useState(null);
  const [accountNumber, setAccountNumber] = useState("");
  const [consumerName, setConsumerName] = useState("");
  const [acknowledgeModalOpen, setAcknowledgeModalOpen] = useState(false);
  const [transactionIdForAcknowledge, setTransactionIdForAcknowledge] = useState(null);
  const [acknowledgeType, setAcknowledgeType] = useState("transaction");

  const columns = [
    {
      label: "ID",
      name: "id",
      options: {
        filter: false,
        sort: true,
      },
    },
    {
      label: "Transaction ID",
      name: "transaction_id",
      options: {
        filter: true,
        sort: true,
      },
    },
    {
      name: "types",
      label: "Type",
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value, tableMeta, updateValue) => {
          if (!value) return "-";
          const types = value.split("||");
          return types.length > 0 ? types[0].charAt(0).toUpperCase() + types[0].slice(1).replaceAll("_", " ") : "-";
        }
      },
    },
    {
      label: "Processed",
      name: "is_processed",
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value, tableMeta, updateValue) => {
          return value ? "✓" : "";
        }
      },
    },
    {
      name: "createdAts",
      label: "Created At",
      options: {
        filter: false,
        sort: true,
        customBodyRender: (value, tableMeta, updateValue) => {
          if (!value) return "-";
          const createdAts = value.split("||");
          if (createdAts.length === 0) return "-";
          return moment.utc(createdAts[0]).local().format("YYYY-MM-DD HH:mm:ss");
        }
      },
    },
    {
      name: "ACTIONS",
      options: {
        filter: false,
        sort: false,
        empty: true,
        customBodyRenderLite: (dataIndex, rowIndex) => {
          const rowData = store.ebt_drs_logs[dataIndex];
          
          // Check if the row type is change_complete or change_pending
          const types = rowData.types ? rowData.types.split("||") : [];
          const isChangeCompleteOrPending = types.length > 0 && ["change_complete", "change_pending", "file"].includes(types[0]);
          
          // Show Ack button only when type is change_complete or change_pending AND is_processed is false
          const showAckButton = isChangeCompleteOrPending && rowData.is_processed === 0;
          
          return (
            <div>
              <Button
                className="btn-rounded mb-2 me-2"
                color="info"
                size="sm"
                onClick={() => {
                  // Parse content and createdAts to create table data
                  const contents = rowData.contents ? rowData.contents.split("||") : [];
                  const types = rowData.types ? rowData.types.split("||") : [];
                  const createdAts = rowData.createdAts ? rowData.createdAts.split("||") : [];
                  
                  // Create array of objects for table display
                  const tableData = contents.map((content, index) => {
                    const parsed = parseXmlContent(content);
                    return {
                      content: parsed,
                      type: types[index] || null,
                      createdAt: createdAts[index] || null
                    };
                  });
                  
                  setContentData(tableData);
                  setTransactionIdForObjection(rowData.transaction_id);
                  setContentDialogOpen(true);
                }}
              >
                Logs
              </Button>
              {showAckButton && (
                <Button
                  className="btn-rounded mb-2 me-2"
                  color="warning"
                  size="sm"
                  onClick={() => {
                    setTransactionIdForAcknowledge(rowData.transaction_id);
                    setAcknowledgeType("transaction");
                    setAcknowledgeModalOpen(true);
                  }}
                  disabled={loadingIds.has(`ack-${rowData.transaction_id}`)}
                >
                  {loadingIds.has(`ack-${rowData.transaction_id}`) ? (
                    <span className="d-flex align-items-center">
                      <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                    </span>
                  ) : (
                    "Send Ack"
                  )}
                </Button>
              )}

              {/* Send Acknowledge Modal */}
              {acknowledgeModalOpen && (
                <div className="modal show d-block" tabIndex="-1" role="dialog">
                  <div className="modal-dialog" role="document">
                    <div className="modal-content">
                      <div className="modal-header">
                        <h5 className="modal-title">Send Acknowledgement</h5>
                      </div>
                      <div className="modal-body">
                        <div className="mb-3">
                          <label htmlFor="acknowledgeType" className="form-label">Type</label>
                          <select
                            id="acknowledgeType"
                            className="form-select"
                            value={acknowledgeType}
                            onChange={(e) => setAcknowledgeType(e.target.value)}
                          >
                            <option value="transaction">Transaction Acknowledgement</option>
                            <option value="termination_transaction">Termination Acknowledgement</option>
                          </select>
                        </div>
                      </div>
                      <div className="modal-footer">
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={() => setAcknowledgeModalOpen(false)}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="btn btn-warning"
                          onClick={async () => {
                            try {
                              const ackLoadingId = `ack-${transactionIdForAcknowledge}`;
                              setLoadingIds(prev => new Set(prev).add(ackLoadingId));
                              
                              // Call backend API with selected acknowledge type
                              await axios.post(`/ebt_logs/transaction/${transactionIdForAcknowledge}/acknowledge`, {
                                acknowledgeType: acknowledgeType
                              });
                              
                              // Refetch data to update the table
                              dispatch(fetchDrsEbtLogs(""));
                              toast.success("Transaction acknowledged");
                              
                              // Close the modal
                              setAcknowledgeModalOpen(false);
                            } catch (error) {
                              toast.error("Failed to acknowledge transaction: " + (error.response?.data?.message || error.message));
                            } finally {
                              setLoadingIds(prev => {
                                const newSet = new Set(prev);
                                newSet.delete(`ack-${transactionIdForAcknowledge}`);
                                return newSet;
                              });
                            }
                          }}
                          disabled={loadingIds.has(`ack-${transactionIdForAcknowledge}`)}
                        >
                          {loadingIds.has(`ack-${transactionIdForAcknowledge}`) ? (
                            <span className="d-flex align-items-center">
                              <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                            </span>
                          ) : (
                            "Send Ack"
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {rowData.is_processed === 0 && (
                <Button
                  className="btn-rounded mb-2 me-2"
                  color="success"
                  size="sm"
                  onClick={async () => {
                    try {
                      // Use a unique identifier for the mark processed button
                      const markProcessedLoadingId = `mark-${rowData.transaction_id}`;
                      setLoadingIds(prev => new Set(prev).add(markProcessedLoadingId));
                      // Make API call to update is_processed to 1 for all rows with this transaction_id
                      await axios.put(`/ebt_logs/transaction/${rowData.transaction_id}`, {
                        is_processed: 1
                      });
                      
                      // Refetch data to update the table
                      dispatch(fetchDrsEbtLogs(""));
                      toast.success("Record marked as processed");
                    } catch (error) {
                      toast.error("Failed to mark record as processed");
                    } finally {
                      setLoadingIds(prev => {
                        const newSet = new Set(prev);
                        newSet.delete(`mark-${rowData.transaction_id}`);
                        return newSet;
                      });
                    }
                  }}
                  disabled={loadingIds.has(`mark-${rowData.transaction_id}`)}
                >
                  {loadingIds.has(`mark-${rowData.transaction_id}`) ? (
                    <span className="d-flex align-items-center">
                      <span className="spinner-border spinner-border-sm mr-2 text-white" role="status" aria-hidden="true"></span>
                    </span>
                  ) : (
                    "Mark Processed"
                  )}
                </Button>
              )}
              {/* Terminate button for acknowledge type and unprocessed rows */}
              {rowData.is_processed === 0 && types.includes('acknowledgement') && (
                <Button
                  className="btn-rounded mb-2 me-2"
                  color="danger"
                  size="sm"
                  onClick={() => {
                    // Set the transaction ID and open the modal
                    setTransactionIdForTermination(rowData.transaction_id);
                    setAccountNumber("");
                    setConsumerName("");
                    setTerminationModalOpen(true);
                  }}
                  disabled={loadingIds.has(`terminate-${rowData.transaction_id}`)}
                >
                  {loadingIds.has(`terminate-${rowData.transaction_id}`) ? (
                    <span className="d-flex align-items-center">
                      <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                    </span>
                  ) : (
                    "Terminate"
                  )}
                </Button>
              )}
            </div>
          );
        },
      },
    },
  ];

  const options = {
    filter: false,
    search: false,
    download: false,
    print: false,
    viewColumns: false,
    count: store.total_ebt_drs_logs || 0,
    serverSide: true,
    onTableChange: (action, tableState) => {
      switch (action) {
        case "changePage":
          setDataOptions(getQueryString(tableState, sortColumns));
          break;
        case "sort":
          if (
            sortColumns.filter(
              (sort) => sort.name === tableState.sortOrder.name
            ).length > 0
          ) {
            // update the existing sortColumn
            let index = sortColumns.findIndex(
              (sort) => sort.name === tableState.sortOrder.name
            );
            sortColumns[index] = {
              name: tableState.sortOrder.name,
              direction: tableState.sortOrder.direction,
            };

            setSortColumns(sortColumns);
          } else {
            // add a new sortColumn
            sortColumns.push({
              name: tableState.sortOrder.name,
              direction: tableState.sortOrder.direction,
            });

            setSortColumns(sortColumns);
          }

          setDataOptions(getQueryString(tableState, sortColumns));
          break;
        case "changeRowsPerPage":
          setDataOptions(getQueryString(tableState, sortColumns));
          break;
      }
    },
  };

  return (
    <div>
      <MUIDataTable
        data={store.ebt_drs_logs || []}
        columns={columns}
        options={options}
      />

      {/* Content Dialog */}
      {contentDialogOpen && (
        <div className="modal show d-block" tabIndex="-1" role="dialog">
          <div className="modal-dialog modal-lg" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">EBT Logs</h5>
              </div>
              <div className="modal-body" style={{ maxHeight: '500px', overflowY: 'auto' }}>
                {contentData && contentData.length > 0 ? (
                  <div className="table-responsive">
                    <table className="table table-striped">
                      <thead>
                        <tr>
                          <th>Type</th>
                          <th>Time</th>
                          <th>Content</th>
                        </tr>
                      </thead>
                      <tbody>
                        {contentData.map((item, index) => {
                          // Check if we have exactly two rows and conditions are met
                          const hasTwoRows = contentData.length === 2;
                          const isFirstRowChangePending = hasTwoRows && contentData[1].type === "change_pending";
                          const isSecondRowAcknowledge = hasTwoRows && contentData[0].type === "acknowledgement";
                          
                          const showObjectionButton = isFirstRowChangePending && isSecondRowAcknowledge;
                          
                          return (
                            <tr key={index}>
                              <td style={{ whiteSpace: 'nowrap' }}>
                                {item.type.charAt(0).toUpperCase() + item.type.slice(1).replaceAll("_", " ") || "-"}
                              </td>
                              <td style={{ whiteSpace: 'nowrap' }}>
                                {item.createdAt ? moment.utc(item.createdAt).local().format("YYYY-MM-DD HH:mm:ss") : "-"}
                              </td>
                              <td>
                                <pre style={{ whiteSpace: 'pre-wrap', wordWrap: 'break-word', marginBottom: '0.5rem' }}>
                                  {item.content.map((line, idx) => {
                                    if (!line.startsWith("Data: ")) {
                                      return <div key={idx}>{line}</div>;
                                    }
                                    return null;
                                  })}
                                </pre>
                                {item.type === "file" && (
                                  <button className="btn btn-sm btn-primary" onClick={() => handleDownload(item)}>
                                    Download
                                  </button>
                                )}
                                {showObjectionButton && index === 1 && (
                                  <button 
                                    className="btn btn-sm btn-danger"
                                    onClick={async () => {
                                      // Show confirmation dialog
                                      const confirmed = window.confirm("Are you sure you want to send objection notification?");
                                      
                                      if (confirmed) {
                                        try {
                                          // Use the transaction ID stored in state
                                          const transactionId = transactionIdForObjection;
                                          
                                          if (!transactionId) {
                                            throw new Error("Transaction ID not available");
                                          }
                                          
                                          // Call backend API to send objection notification
                                          const response = await axios.post(`/ebt_logs/transaction/${transactionId}/objection`);
                                          
                                          // Show success message
                                          toast.success("Objection notification sent successfully");
                                          
                                          // Close the dialog
                                          setContentDialogOpen(false);
                                        } catch (error) {
                                          console.error("Failed to send objection notification:", error);
                                          toast.error("Failed to send objection notification: " + (error.response?.data?.message || error.message));
                                        }
                                      }
                                    }}
                                  >
                                    Send Objection
                                  </button>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p>No content available</p>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setContentDialogOpen(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Termination Modal */}
      {terminationModalOpen && (
        <div className="modal show d-block" tabIndex="-1" role="dialog">
          <div className="modal-dialog" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Terminate EBT Process</h5>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label htmlFor="accountNumber" className="form-label">Account Number</label>
                  <input
                    type="text"
                    className="form-control"
                    id="accountNumber"
                    value={accountNumber}
                    onChange={(e) => setAccountNumber(e.target.value)}
                    placeholder="Enter Account Number"
                  />
                </div>
                <div className="mb-3">
                  <label htmlFor="consumerName" className="form-label">Consumer Name</label>
                  <input
                    type="text"
                    className="form-control"
                    id="consumerName"
                    value={consumerName}
                    onChange={(e) => setConsumerName(e.target.value)}
                    placeholder="Enter Consumer Name"
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setTerminationModalOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={async () => {
                    // Validate inputs
                    if (!accountNumber.trim() || !consumerName.trim()) {
                      toast.error("Account Number and Consumer Name are required");
                      return;
                    }

                    try {
                      // Use a unique identifier for the terminate button
                      const terminateLoadingId = `terminate-${transactionIdForTermination}`;
                      setLoadingIds(prev => new Set(prev).add(terminateLoadingId));
                      
                      // Make API call to send termination notification with account number and consumer name
                      await axios.post(`/ebt_logs/transaction/${transactionIdForTermination}/terminate`, {
                        accountNumber: accountNumber.trim(),
                        consumerName: consumerName.trim()
                      });
                      
                      // Refetch data to update the table
                      dispatch(fetchDrsEbtLogs(""));
                      toast.success("Termination notification sent successfully");
                      
                      // Close the modal
                      setTerminationModalOpen(false);
                    } catch (error) {
                      toast.error("Failed to send termination notification: " + (error.response?.data?.message || error.message));
                    } finally {
                      setLoadingIds(prev => {
                        const newSet = new Set(prev);
                        newSet.delete(`terminate-${transactionIdForTermination}`);
                        return newSet;
                      });
                    }
                  }}
                  disabled={loadingIds.has(`terminate-${transactionIdForTermination}`)}
                >
                  {loadingIds.has(`terminate-${transactionIdForTermination}`) ? (
                    <span className="d-flex align-items-center">
                      <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                    </span>
                  ) : (
                    "Terminate"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EBTDrsInbox;